import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "./ui/button";

const TOUR_STEPS = [
  {
    target: '[data-testid="nav-dashboard"]',
    title: "Dashboard",
    content: "Your command center. See upcoming tasks, overdue items, and a quick summary of all your transactions at a glance.",
    position: "right",
  },
  {
    target: '[data-testid="nav-transactions"]',
    title: "Transactions",
    content: "Create and manage your real estate contracts here. Each transaction tracks key dates, parties, tasks, documents, and financial details.",
    position: "right",
  },
  {
    target: '[data-testid="nav-workflows"]',
    title: "Workflows",
    content: "Build reusable task checklists. Set up templates like 'Buyer Side - Single Family' with auto-calculated due dates, email templates, and party assignments.",
    position: "right",
  },
  {
    target: '[data-testid="nav-contacts"]',
    title: "Contacts",
    content: "Your people database. Store buyer, seller, lender, and agent info. Import contacts directly from Follow Up Boss.",
    position: "right",
  },
  {
    target: '[data-testid="nav-calendar"]',
    title: "Calendar",
    content: "Visual timeline of all deadlines across your transactions. Export to Google Calendar or download an ICS file.",
    position: "right",
  },
  {
    target: '[data-testid="nav-reports"]',
    title: "Reports",
    content: "Analytics on your pipeline, task completion rates, financial volume, coordinator performance, and more. Filter by date range.",
    position: "right",
  },
  {
    target: '[data-testid="nav-settings"]',
    title: "Settings",
    content: "Configure your email (SMTP), set your sender name and signature, choose a theme, and customize your experience.",
    position: "right",
  },
  {
    target: '[data-testid="nav-help-center"]',
    title: "Help Center",
    content: "You can always come back here for detailed guides on every feature. You can also restart this tour anytime from the Help page.",
    position: "right",
  },
];

const OnboardingTour = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const forceTour = params.get("tour") === "1";
    const completed = localStorage.getItem("onboarding_complete");

    if (forceTour || !completed) {
      // Small delay to let the page render
      const timer = setTimeout(() => setActive(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    updatePosition();
    const handleResize = () => updatePosition();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [active, step]);

  const updatePosition = () => {
    const currentStep = TOUR_STEPS[step];
    if (!currentStep) return;
    const el = document.querySelector(currentStep.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  };

  const finish = () => {
    setActive(false);
    localStorage.setItem("onboarding_complete", "true");
    // Clean up URL params
    const url = new URL(window.location);
    url.searchParams.delete("tour");
    window.history.replaceState({}, "", url.pathname);
  };

  const next = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(step + 1);
    } else {
      finish();
    }
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!active) return null;

  const currentStep = TOUR_STEPS[step];

  // Calculate tooltip position
  const tooltipStyle = {};
  if (currentStep.position === "right") {
    tooltipStyle.top = position.top;
    tooltipStyle.left = position.left + position.width + 16;
  } else if (currentStep.position === "bottom") {
    tooltipStyle.top = position.top + position.height + 16;
    tooltipStyle.left = position.left;
  }

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-tour">
      {/* Dark overlay with cutout */}
      <div className="absolute inset-0 bg-black/50" onClick={finish} />

      {/* Spotlight cutout */}
      <div
        className="absolute rounded-lg ring-4 ring-white/80 bg-transparent z-[10000] transition-all duration-300 ease-in-out"
        style={{
          top: position.top - 4,
          left: position.left - 4,
          width: position.width + 8,
          height: position.height + 8,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
        }}
      />

      {/* Tooltip Card */}
      <div
        className="absolute z-[10001] bg-white rounded-xl shadow-2xl p-5 w-[320px] transition-all duration-300 ease-in-out"
        style={tooltipStyle}
        data-testid="tour-tooltip"
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1">
            {TOUR_STEPS.map((s, i) => (
              <div
                key={s.title}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-[var(--t-primary)]" : i < step ? "w-1.5 bg-[var(--t-primary)]/50" : "w-1.5 bg-slate-200"
                }`}
              />
            ))}
          </div>
          <button onClick={finish} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="tour-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <h3 className="text-base font-bold text-[var(--t-text)] mb-1.5">{currentStep.title}</h3>
        <p className="text-sm text-[var(--t-text-muted)] leading-relaxed mb-4">{currentStep.content}</p>

        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={prev}
            disabled={step === 0}
            className="text-xs"
            data-testid="tour-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back
          </Button>
          <span className="text-[11px] text-[var(--t-text-subtle)]">{step + 1} of {TOUR_STEPS.length}</span>
          <Button
            size="sm"
            onClick={next}
            className="text-xs bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]"
            data-testid="tour-next"
          >
            {step === TOUR_STEPS.length - 1 ? "Finish" : "Next"} <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
